import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { SYMBOLS, fmtMoney } from "../components/proposal/format";
import { ProposalLoadingScreen } from "../components/proposal/LoadingScreen";
import { TabButton } from "../components/proposal/shared";
import { Accreditations, HeroBand } from "../components/proposal/HeroBand";
import { SpecsTab } from "../components/proposal/SpecsTab";
import { AssumptionsTab } from "../components/proposal/AssumptionsTab";
import { adjustPivot, applyIncentiveCeil5, reconcileDisplayTotals } from "../components/proposal/costMath";
import { CostsTab } from "../components/proposal/CostsTab";
import { TimelinesTab } from "../components/proposal/TimelinesTab";
import { TermsTab } from "../components/proposal/TermsTab";
import { ForwardToColleagueButton } from "../components/proposal/ForwardToColleagueButton";
import { useShareAnalytics } from "../hooks/useShareAnalytics";
import { useCommission } from "../hooks/useCommission";
import { CommissionPanel } from "../components/commission/CommissionPanel";
import { StaffDeadlinePanel } from "../components/commission/StaffDeadlinePanel";
import { StaffCountersignPanel } from "../components/commission/StaffCountersignPanel";
import { StaffSpecialConditionsPanel } from "../components/commission/StaffSpecialConditionsPanel";
import { CommissionButton } from "../components/commission/CommissionButton";
import usePublicAttachments from "../hooks/usePublicAttachments";
import ShareGate from "../components/share/ShareGate";
import AttachmentBanner from "../components/share/AttachmentBanner";
import AttachmentGateDialog from "../components/share/AttachmentGateDialog";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicProposal() {
  const { token } = useParams();
  // ``?as=staff`` lets internal users open a proposal for QA without
  // polluting the client-facing analytics. The tracking hook tags
  // those sessions with ``source: "staff-preview"`` so the Share
  // dashboard filters them out by default.
  // ``?as=client`` is the inverse override - staff clicks "View as
  // client" in the wizard to QA the actual client experience
  // (email gate prompt + domain allowlist). It MUST be forwarded
  // to every backend call so ``_check_share_gate`` and ``gate_status``
  // can suppress the staff cookie bypass.
  const [searchParams] = useSearchParams();
  const asParam = searchParams.get("as") || "";
  const trackingSource = asParam === "staff" ? "staff-preview" : "client";
  // Build a "?as=..." query suffix that we append to EVERY public
  // proposal API call so the backend sees the same override the
  // user explicitly chose in the URL. Without forwarding this, the
  // user lands on /p/{token}?as=client but the backend gate-status
  // call drops the param and the staff cookie wins, silently
  // bypassing the gate the user is trying to QA.
  const asQuery = asParam ? `?as=${encodeURIComponent(asParam)}` : "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  // Gate state - null = checking, {required, satisfied, mode} once
  // /gate/status responds. While `gate.required && !gate.satisfied`
  // we render <ShareGate /> instead of the proposal body.
  const [gate, setGate] = useState(null);
  // Staff announcement shown on the loading screen; the client must
  // press Continue before the proposal appears. Fetched together with
  // the gate status so it is known before the loading screen renders.
  const [announcement, setAnnouncement] = useState(null);
  const [announcementSeen, setAnnouncementSeen] = useState(false);
  const [tab, setTab] = useState("specs");
  const [targetCurrency, setTargetCurrency] = useState("GBP");
  const [targetRate, setTargetRate] = useState(1);
  // ---- cost sandbox state (lifted to the page so adjustments survive
  // tab switches and the Timelines tab can react to sample increases) ----
  const [sampleOverrides, setSampleOverrides] = useState({});
  const [unitOverrides, setUnitOverrides] = useState({});
  const [editMode, setEditMode] = useState(false);
  // "Have they read the assumptions yet?", drives the pulsing red
  // dot on the Assumptions tab. Resets on every page load so each
  // new visit re-nags the client to read the assumptions, this is a
  // deliberate "every-time" warning, not a "first-visit" hint.
  const [assumptionsSeen, setAssumptionsSeen] = useState(false);
  // Attachment blocked by the assumptions gate, drives the
  // "Before you download" dialog.
  const [blockedAttachment, setBlockedAttachment] = useState(null);
  const openAssumptions = () => {
    if (!assumptionsSeen) {
      setAssumptionsSeen(true);
    }
    setTab("assumptions");
  };
  // Per-visit acknowledgement state for the Costs and Timelines
  // tab-open disclaimers. Held at the page level so tab switches
  // within a single visit don't re-prompt, but a page reload (every
  // fresh client visit) resets both flags so the warnings fire again.
  const [costsAcknowledged, setCostsAcknowledged] = useState(false);
  const [timelinesAcknowledged, setTimelinesAcknowledged] = useState(false);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const narrativeFetchedRef = useRef(false);
  const narrativeInFlightRef = useRef(false);
  // Supplier-quote overlay state. Server-side a staff member can
  // toggle ``active=true`` once they've picked winning bids for some
  // (country × service) cells; we fetch the resolved map here and
  // pass it into CostsTab so the public viewer renders a "costs
  // verified with delivery partners" banner. Cell-level numeric
  // overrides will be plugged in once staff have done a full pass on
  // real RFQs, for now the toggle is the source of truth and the
  // banner is the visible signal.
  const [supplierOverlay, setSupplierOverlay] = useState(null);

  // Check the gate first - until it's satisfied (or `required:false`),
  // we don't fetch the proposal body. This avoids a flash of the
  // proposal during the gate handshake on a cold load.
  useEffect(() => {
    let cancelled = false;
    setGate(null);
    (async () => {
      const announcementReq = axios
        .get(`${API}/api/public/announcements/active`)
        .then((r) => r.data?.announcement || null)
        .catch(() => null);
      try {
        const { data: g } = await axios.get(
          `${API}/api/public/proposals/${encodeURIComponent(token)}/gate/status${asQuery}`,
          { withCredentials: true },
        );
        const a = await announcementReq;
        if (!cancelled) {
          setAnnouncement(a);
          setGate(g || { required: false, satisfied: true });
        }
      } catch (e) {
        const a = await announcementReq;
        if (!cancelled) {
          // 404 → unknown token; let the proposal-fetch surface the
          // friendlier error. Anything else: treat as ungated so
          // the proposal can attempt to load and propagate its
          // own error if needed.
          setAnnouncement(a);
          setGate({ required: false, satisfied: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, asQuery]);

  useEffect(() => {
    if (!gate || !gate.satisfied) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const { data: d } = await axios.get(
          `${API}/api/public/proposals/${encodeURIComponent(token)}${asQuery}`,
          { withCredentials: true },
        );
        if (!cancelled) setData(d);
      } catch (e) {
        if (cancelled) return;
        // If the gate cookie expired between the check and this
        // fetch, recover gracefully: flip the gate UI back on.
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 403 && detail?.reason === "GATE_REQUIRED") {
          setGate({ required: true, satisfied: false, mode: detail.mode });
          return;
        }
        setErr(
          (typeof detail === "string" ? detail : null) ||
            "Unable to load this proposal.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, gate, asQuery]);

  // Lazy-fetch the narrative (Study Specs + Assumptions from the DOCX)
  // the first time the main payload lands. We track in-flight status via
  // refs (NOT state) so the effect doesn't synchronously set state on
  // mount, keeps React's `set-state-in-effect` lint rule happy.
  useEffect(() => {
    if (!data) return;
    if (narrativeFetchedRef.current || narrativeInFlightRef.current) return;
    narrativeInFlightRef.current = true;
    let cancelled = false;
    (async () => {
      setNarrativeLoading(true);
      try {
        const { data: n } = await axios.get(
          `${API}/api/public/proposals/${encodeURIComponent(token)}/narrative`
        );
        if (!cancelled) {
          setData((prev) => ({ ...prev, ...n }));
        }
      } catch (_e) {
        // Soft-fail, fall back to the JSON narrative fields.
      } finally {
        narrativeFetchedRef.current = true;
        narrativeInFlightRef.current = false;
        if (!cancelled) setNarrativeLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data, token]);

  // Pull the supplier-quote overlay (best-effort, endpoint always
  // returns 200, with active=false when no picks exist or staff have
  // hidden it). Runs in parallel with the main payload to avoid
  // blocking the page on a slow Mongo round-trip.
  useEffect(() => {
    // Wait for the gate handshake: the overlay endpoint now enforces
    // the same email gate as the main payload, so fetching before
    // the cookie is set would 403 and never retry.
    if (!gate || (gate.required && !gate.satisfied)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data: ov } = await axios.get(
          `${API}/api/public/proposals/${encodeURIComponent(token)}/supplier-cost-overlay`
        );
        if (!cancelled) setSupplierOverlay(ov);
      } catch (_e) {
        // Overlay is non-essential, silently ignore so a 404/500
        // here never breaks the proposal viewer for the client.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, gate]);

  // Currency conversion helpers, applied at the leaf cells.
  const convert = (n) =>
    targetCurrency === "GBP" ? n : (n || 0) * (Number(targetRate) || 0);
  const sym = SYMBOLS[targetCurrency] || "£";
  const money = (n) =>
    fmtMoney(convert(n), sym, targetCurrency === "JPY" ? 0 : 2);

  // Sandbox-adjusted pivots, shared by the Costs and Timelines tabs.
  // A second pass rounds participant-incentive per-unit values per
  // the currency-driven rule: floor-5 mid (= local payout) when the
  // displayed currency matches the market's native currency,
  // ceil-5 buffered otherwise. GBP display is a no-op.
  const adjusted = useMemo(
    () =>
      (data?.phase_pivots || []).map((p, pi) => {
        const a = adjustPivot(p, pi, sampleOverrides, unitOverrides);
        return {
          ...a,
          // Final pass: snap every converted cell to a whole displayed
          // penny and re-sum subtotals/grand from those values, so the
          // on-screen column adds up AND matches the Excel export.
          phase: reconcileDisplayTotals(
            applyIncentiveCeil5(
              a.phase,
              targetCurrency,
              targetRate,
              data?.country_fx
            ),
            targetCurrency,
            targetRate
          ),
        };
      }),
    [
      data?.phase_pivots,
      data?.country_fx,
      sampleOverrides,
      unitOverrides,
      targetCurrency,
      targetRate,
    ]
  );
  const samplesIncreased = adjusted.some((a) => a.anyIncrease);

  // Show the Timelines tab when this proposal has per-phase working-day
  // rows, Project Timings narrative paragraphs, OR an assumed live
  // period quoted in weeks/months (e.g. 6703 → 38 weeks, 6833 → 24
  // months via Monthly Study Management). Only when none of those exist
  // do we hide the tab. We wait for the narrative endpoint to finish
  // before declaring "empty" so we don't briefly hide a tab while
  // paragraphs are still streaming in. If the user landed on the
  // hidden Timelines tab via a saved link, fall back to Specs in
  // render rather than via an effect.
  const hasTimelines =
    (data?.timelines?.length || 0) > 0 ||
    (data?.narrative_timelines?.length || 0) > 0 ||
    !!data?.assumed_duration;
  const showTimelinesTab = hasTimelines || narrativeLoading;
  // Client side commissioning (SOW phase 2). Same gate as the body;
  // the tab only appears once the state has loaded and the deal is
  // not closed in SharePoint / Cognito. Staff previews (cookie bypass)
  // never show it; staff use ?as=client to walk the client flow.
  const clientView = !!gate && (!gate.required || gate.satisfied) && !gate.as_staff;
  const commission = useCommission(token, asQuery, clientView);
  const showCommissionTab = clientView && !!commission.state && !commission.state.closed;
  const effectiveTab =
    (tab === "timelines" && !showTimelinesTab) || (tab === "commission" && !showCommissionTab) ? "specs" : tab;

  // Size the Costs pane to its content: ~160px per value column
  // (countries + Total) on top of the 260px label column, clamped so a
  // single-country grid stays compact while 8+ countries still fit the
  // previous 1400px ceiling.
  const costsMaxWidth = useMemo(() => {
    let cols = 0;
    for (const p of data?.phase_pivots || []) {
      cols = Math.max(cols, (p.countries || []).length + 1); // + Total col (always shown)
    }
    return Math.min(1400, Math.max(820, 260 + cols * 160 + 96));
  }, [data]);

  // Fire open / tab_change / heartbeat / close events to the
  // backend analytics endpoint. The hook reads ``effectiveTab`` so
  // dwell time is attributed to the tab the user is actually
  // looking at, not the URL hash. No-op on the loading / error
  // paths because they short-circuit before this render.
  //
  // Also returns `recordEvent` which we hand down to CostsTab so
  // it can emit `download`, `sample_change`, and `fx_switch`
  // signals - those drive the deal-engagement panel on /deals.
  const trackingEnabled = !!gate && (!gate.required || gate.satisfied);
  const { recordEvent } = useShareAnalytics(
    token, effectiveTab, trackingSource, trackingEnabled
  );

  const openCommission = () => {
    setTab("commission");
    recordEvent("commission_open", { meta: { stage: commission.state?.stage } });
    if (typeof window !== "undefined") {
      window.scrollTo({ top: Math.max(0, (document.getElementById("proposal-tabs")?.offsetTop || 0) - 8), behavior: "smooth" });
    }
  };

  // Lift share-link attachments to the page so both the slim banner
  // at the very top of the proposal AND the prominent download card
  // inside the Costs tab share a single fetch + download flow. This
  // is the fix for "clients can't find the cost grid I uploaded":
  // the file now appears in two visually distinct places - the top
  // banner is the immediate cue when the page first paints, and the
  // in-Costs card guarantees they see it when they hit the pricing
  // section (where they instinctively look for a cost spreadsheet).
  //
  // `ready` is gated on the gate handshake clearing - calling the
  // attachments endpoint before the gate cookie is set returns 403,
  // which would silently cache an empty list and hide both surfaces
  // even when attachments exist on the link.
  const attachmentsReady = !!gate && (!gate.required || gate.satisfied);
  const {
    items: attachments,
    downloadingId: downloadingAttachmentId,
    downloadAttachment,
  } = usePublicAttachments(
    token,
    (meta) => recordEvent("download", meta),
    attachmentsReady,
  );

  // Attachment downloads are gated on the client having opened the
  // Assumptions tab this visit. A blocked click opens the disclaimer
  // dialog instead; once assumptions are seen, downloads flow
  // straight through.
  const guardedDownloadAttachment = (att) => {
    if (!assumptionsSeen) {
      setBlockedAttachment(att);
      return;
    }
    downloadAttachment(att);
  };

  // Gate has the highest priority - even before the loading screen.
  // If we're still checking gate status, show the loading screen so
  // we don't flash an empty page or a wrong gate state.
  if (gate === null) {
    return <ProposalLoadingScreen />;
  }
  if (gate.required && !gate.satisfied) {
    return (
      <ShareGate
        token={token}
        mode={gate.mode}
        onPass={(asEmail) =>
          setGate({ ...gate, satisfied: true, as_email: asEmail || null })
        }
      />
    );
  }

  const holdForAnnouncement = !!announcement && !announcementSeen && !gate.as_staff && !err;
  if (loading || holdForAnnouncement) {
    return (
      <ProposalLoadingScreen
        announcement={holdForAnnouncement ? announcement : null}
        ready={!loading}
        onContinue={() => setAnnouncementSeen(true)}
      />
    );
  }
  if (err) {
    return (
      <div className="min-h-screen bg-[#0B1220] text-stone-100 flex items-center justify-center font-sans p-6">
        <div className="max-w-md text-center" data-testid="proposal-error">
          <img
            src="/brand/medicys-logo.png"
            alt="Medicys"
            className="h-12 mx-auto mb-8 opacity-90 w-auto"
          />
          <p className="text-xs font-mono tracking-wider uppercase text-[#3EA8DC] mb-2">
            Sorry
          </p>
          <h1 className="text-2xl font-serif font-light mb-3">
            We couldn&apos;t load this proposal.
          </h1>
          <p className="text-sm text-stone-400">{err}</p>
        </div>
      </div>
    );
  }

  const d = data || {};

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 font-sans antialiased" data-testid="public-proposal-root">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-page-break { page-break-before: always; }
          body { background: white !important; }
          .hero-band { background: #0B1220 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .show-on-print { display: block !important; }
        }
        @page { margin: 14mm 12mm; size: A4; }
      `}</style>

      <AttachmentBanner
        items={attachments}
        downloadingId={downloadingAttachmentId}
        downloadAttachment={guardedDownloadAttachment}
      />

      <AttachmentGateDialog
        attachment={blockedAttachment}
        onClose={() => setBlockedAttachment(null)}
        onReviewAssumptions={() => {
          setBlockedAttachment(null);
          openAssumptions();
        }}
      />

      {gate?.as_staff && (
        <div
          className="no-print bg-amber-50 border-b border-amber-200 text-amber-900 text-xs sm:text-sm px-4 sm:px-6 lg:px-10 py-2 flex items-center gap-3"
          data-testid="staff-bypass-banner"
        >
          <span
            className="inline-flex items-center gap-1.5 font-mono uppercase tracking-[0.14em] text-[10px] bg-amber-200/70 text-amber-900 px-2 py-0.5 rounded"
          >
            Staff preview
          </span>
          <span className="flex-1 leading-relaxed">
            You are bypassing the client gate. To see what a real
            client sees (email prompt, forward-to-colleague button,
            commissioning), append{" "}
            <code className="font-mono bg-amber-100 px-1 rounded">
              ?as=client
            </code>{" "}
            to the URL.
          </span>
          <a
            href={
              typeof window !== "undefined"
                ? window.location.href.includes("?")
                  ? window.location.href.replace(/(\?|&)as=[^&]*/, "") +
                    (window.location.href.replace(/(\?|&)as=[^&]*/, "").includes("?") ? "&" : "?") +
                    "as=client"
                  : `${window.location.href}?as=client`
                : "?as=client"
            }
            data-testid="staff-bypass-view-as-client"
            className="inline-flex items-center gap-1 bg-amber-900 hover:bg-amber-800 text-amber-50 text-[10px] font-mono uppercase tracking-[0.12em] px-2.5 py-1 rounded transition-colors"
          >
            View as client
          </a>
        </div>
      )}
      {gate?.as_staff && <StaffCountersignPanel token={token} onExecuted={commission.reload} />}
      {gate?.as_staff && <StaffSpecialConditionsPanel token={token} onChanged={commission.reload} />}
      {gate?.as_staff && <StaffDeadlinePanel token={token} />}

      <HeroBand d={d} action={showCommissionTab ? <CommissionButton commission={commission} d={d} onOpenTab={openCommission} active={effectiveTab === "commission"} variant="hero" /> : null} />

      {/* Sticky tab bar, on phones it stacks: tab strip scrolls
          horizontally, PDF buttons drop to a second row that also
          scrolls so they never wrap into a cluttered mess. */}
      <div id="proposal-tabs" className="sticky top-0 z-20 bg-stone-50/95 backdrop-blur border-b border-stone-200 no-print">
        <div className="mx-auto max-w-5xl xl:max-w-6xl px-4 sm:px-6 lg:px-10 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
          <div
            className="inline-flex bg-stone-200/60 rounded-lg p-1 overflow-x-auto whitespace-nowrap max-w-full -mx-1 px-1 scrollbar-thin"
            role="tablist"
          >
            <TabButton active={effectiveTab === "specs"} onClick={() => setTab("specs")} testid="tab-specs">
              Specifications
            </TabButton>
            <TabButton active={effectiveTab === "assumptions"} onClick={openAssumptions} testid="tab-assumptions">
              Assumptions
              {!assumptionsSeen && (
                <span
                  className="relative inline-flex h-2 w-2 ml-1.5 align-middle"
                  data-testid="assumptions-attention-dot"
                >
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
              )}
            </TabButton>
            <TabButton active={effectiveTab === "costs"} onClick={() => setTab("costs")} testid="tab-costs">
              Costs
            </TabButton>
            {showTimelinesTab && (
              <TabButton active={effectiveTab === "timelines"} onClick={() => setTab("timelines")} testid="tab-timelines">
                Timelines
              </TabButton>
            )}
            <TabButton active={effectiveTab === "terms"} onClick={() => setTab("terms")} testid="tab-terms">
              Terms
            </TabButton>
          </div>
          <div className="flex items-center gap-2 shrink-0 overflow-x-auto whitespace-nowrap -mx-1 px-1 scrollbar-thin">
            {gate?.as_email && !gate?.as_staff && (
              <ForwardToColleagueButton
                apiBaseUrl={API}
                token={token}
                senderEmail={gate.as_email}
              />
            )}
          </div>
        </div>
      </div>

      <main
        className="mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10 lg:py-14 transition-[max-width] duration-300"
        style={{ maxWidth: effectiveTab === "costs" ? costsMaxWidth : 1024 }}
      >
        {effectiveTab === "specs" && (
          <SpecsTab d={d} narrativeLoading={narrativeLoading} />
        )}
        {effectiveTab === "assumptions" && (
          <AssumptionsTab d={d} narrativeLoading={narrativeLoading} />
        )}
        {effectiveTab === "costs" && (
          <CostsTab
            d={d}
            money={money}
            targetCurrency={targetCurrency}
            targetRate={targetRate}
            setTargetCurrency={setTargetCurrency}
            setTargetRate={setTargetRate}
            adjusted={adjusted}
            sampleOverrides={sampleOverrides}
            setSampleOverrides={setSampleOverrides}
            unitOverrides={unitOverrides}
            setUnitOverrides={setUnitOverrides}
            editMode={editMode}
            setEditMode={setEditMode}
            exportUrl={`${API}/api/public/proposals/${token}/proposal.xlsx`}
            pdfExportUrl={`${API}/api/public/proposals/${token}/proposal.pdf`}
            supplierOverlay={supplierOverlay}
            acknowledged={costsAcknowledged}
            setAcknowledged={setCostsAcknowledged}
            recordEvent={recordEvent}
          />
        )}
        {effectiveTab === "timelines" && showTimelinesTab && (
          <TimelinesTab
            d={d}
            narrativeLoading={narrativeLoading}
            samplesIncreased={samplesIncreased}
            acknowledged={timelinesAcknowledged}
            setAcknowledged={setTimelinesAcknowledged}
            recordEvent={recordEvent}
          />
        )}
        {effectiveTab === "terms" && (
          <TermsTab d={d} narrativeLoading={narrativeLoading} />
        )}
        {effectiveTab === "commission" && showCommissionTab && (
          <CommissionPanel d={d} commission={commission} />
        )}

        <footer className="mt-12 sm:mt-20 pt-8 border-t border-stone-200 text-xs text-stone-500 flex flex-col gap-6" data-testid="proposal-footer">
          <Accreditations />
          <p>
            Prepared by Medicys · Healthcare Fieldwork Experts ·{" "}
            <a
              href="https://medicysltd.co.uk"
              className="hover:text-stone-800"
              target="_blank"
              rel="noreferrer"
            >
              medicysltd.co.uk
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
}
